import { ReactNode, RefObject } from 'react';

/**
 * A layout component for modal windows with a consistent structure.
 *
 * Usage:
 * ```tsx
 * <ModalLayout.Root>
 *   Optional step indicators, titles etc
 *   <ModalLayout.Stage>
 *     <ModalLayout.Sidebar>
 *       Sidebar content like instructions, forms etc
 *     </ModalLayout.Sidebar>
 *     <ModalLayout.Main>
 *       Main content area, typically a 3D canvas or similar
 *     </ModalLayout.Main>
 *   </ModalLayout.Stage>
 *   <ModalLayout.Buttons>
 *     Action buttons like Cancel/Confirm
 *   </ModalLayout.Buttons>
 * </ModalLayout.Root>
 * ```
 */

const Root = ({ children }: { children?: ReactNode }) => {
  return <div className="flex h-full min-h-0 flex-col gap-4">{children}</div>;
};

const Stage = ({
  children,
  ref,
}: {
  children?: ReactNode;
  ref?: RefObject<HTMLDivElement | null>;
}) => (
  <div
    ref={ref}
    className="relative flex min-h-0 flex-1 flex-col-reverse items-stretch rounded-2xl sm:aspect-1167/600 md:flex-row md:gap-4"
    style={{
      backgroundImage: 'url("/images/3d-bg.jpg")',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    }}
  >
    {children}
  </div>
);

const Main = ({ children }: { children?: ReactNode }) => (
  <div className="relative min-h-0 w-full flex-1 rounded-2xl md:min-w-0">
    {children}
  </div>
);

const Sidebar = ({ children }: { children?: ReactNode }) => (
  <div className="styled-scrollbar dark:bg-gray-90 m-4 mt-0 flex max-h-[35vh] min-h-0 flex-none flex-col overflow-y-auto rounded-lg bg-white p-4 md:mt-4 md:mr-0 md:max-h-none md:w-[290px]">
    {children}
  </div>
);

const Buttons = ({ children }: { children?: ReactNode }) => (
  <div className="flex flex-none justify-between gap-4 pb-5 md:pb-0">
    {children}
  </div>
);

export default {
  Root,
  Stage,
  Main,
  Sidebar,
  Buttons,
};
